/**
 * Active chats (glossary): chats this person wrote to within the window,
 * loaded from the CRM, kept fresh from the socket's chat states and the
 * person's own sends, and reloaded every 2 minutes. Shared by the Workspace
 * widget and the Chats side column's dock.
 */
import { useEffect, useState } from 'react';
import { fetchActiveChats } from './api';
import { applyChatState, applySend, windowHours, type ActiveChat, type ActiveWindow, type SendEvent } from './chats';
import type { WorkspaceContextValue } from './WorkspaceProvider';

const RELOAD_MS = 120_000;

export function useActiveChats(ws: Pick<WorkspaceContextValue, 'enabled' | 'me' | 'onEvent' | 'onChatStates'>, activeWindow: ActiveWindow, on = true) {
  const [active, setActive] = useState<ActiveChat[]>([]);
  const [loaded, setLoaded] = useState(false);
  const live = ws.enabled && on;

  useEffect(() => {
    if (!live) return;
    let alive = true;
    const load = () =>
      fetchActiveChats(windowHours(activeWindow, Date.now()))
        .then((rows) => alive && (setActive(rows), setLoaded(true)))
        .catch(() => alive && setLoaded(true));
    load();
    const timer = window.setInterval(load, RELOAD_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [live, activeWindow]);

  const { onChatStates, onEvent } = ws;
  useEffect(() => (live ? onChatStates((rows) => setActive((prev) => rows.reduce((acc, row) => applyChatState(acc, row), prev))) : undefined), [live, onChatStates]);

  const myId = ws.me?.id;
  useEffect(
    () => (live && myId ? onEvent((ev) => ev.kind === 'team_sent' && setActive((prev) => applySend(prev, ev as unknown as SendEvent, myId))) : undefined),
    [live, onEvent, myId],
  );

  return { active, loaded };
}
